import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { SkeletonModule } from 'primeng/skeleton';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { Equipamento } from '@core/models/models';
import { FeedbackService } from '@core/services/feedback.service';
import { ManutencaoEquipamentoService } from '@shared/api/services/manutencao-equipamento.service';
import { EnumService } from '@shared/api/services/enum.service';
import {
  ManutencaoEquipamento,
  ManutencaoEquipamentoForm,
} from '@shared/api/models/manutencao-equipamento.model';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import {
  EquipamentoKpiGridComponent,
  EquipamentoKpiItem,
} from '../equipamento-kpi-grid/equipamento-kpi-grid.component';
import { EquipmentPhotoComponent } from '../equipment-photo/equipment-photo.component';
import { forkJoin, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-frota-manutencao-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SkeletonModule,
    DrawerModule,
    InputTextModule,
    TooltipModule,
    SelectModule,
    DatePickerModule,
    InputNumberModule,
    TextareaModule,
    MenuModule,
    EmptyStateComponent,
    EquipamentoKpiGridComponent,
    EquipmentPhotoComponent,
  ],
  templateUrl: './frota-manutencao-tab.component.html',
  styleUrl: './frota-manutencao-tab.component.scss',
})
export class FrotaManutencaoTabComponent implements OnInit {
  private readonly service = inject(ManutencaoEquipamentoService);
  private readonly enumService = inject(EnumService);
  private readonly feedback = inject(FeedbackService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() equipamentos: Equipamento[] = [];
  @Output() countChange = new EventEmitter<number>();

  @ViewChild('rowMenu') rowMenu!: any;

  rows: ManutencaoEquipamento[] = [];
  filteredRows: ManutencaoEquipamento[] = [];
  menuItems: MenuItem[] = [];
  loading = false;

  // Filtros
  searchTerm = '';
  activeFilter = 'Todas'; // 'Todas' | 'Em Curso' | 'Agendada' | 'Concluída' | 'Aguarda Peças'

  // KPIs
  kpiItems: EquipamentoKpiItem[] = [];

  // Drawer de Registo/Edição
  drawerVisible = false;
  editingId: number | null = null;
  saving = false;
  submitted = false;
  selectedFiles: File[] = [];

  form: ManutencaoEquipamentoForm = this.emptyForm();

  // Drawer de Conclusão
  concluirDrawerVisible = false;
  savingConclusao = false;
  concluindoRow: ManutencaoEquipamento | null = null;
  concluirForm = {
    dataSaida: new Date() as Date | null,
    custoReal: null as number | null,
    kmReal: null as number | null,
    oficina: '',
    oficinaContacto: '',
    observacoes: '',
  };
  concluirSelectedFiles: File[] = [];

  // Drawer de Detalhes
  detalheDrawerVisible = false;
  detalheRow: ManutencaoEquipamento | null = null;

  tipos: { label: string; value: number }[] = [];
  estados: { label: string; value: number }[] = [];

  get availableEquipamentosOpcoes() {
    return this.equipamentos
      .filter((e) => !!e.id)
      .map((e) => ({
        label: `${this.getMatricula(e)} - ${this.getMarcaModelo(e)}`,
        value: e.id,
      }));
  }

  ngOnInit(): void {
    forkJoin({
      tipos: this.enumService.getEnum('TipoManutencaoEquipamento'),
      estados: this.enumService.getEnum('ManutencaoEstado'),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.tipos = Object.entries(res.tipos).map(([k, v]) => ({
            label: v,
            value: Number(k),
          }));
          this.estados = Object.entries(res.estados).map(([k, v]) => ({
            label: v,
            value: Number(k),
          }));
          this.load();
        },
        error: () => {
          this.load();
        },
      });
  }

  load(): void {
    this.loading = true;
    this.service
      .listar({ pageNumber: 1, pageSize: 1000 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.rows = (response?.data ?? []).sort(
            (a, b) =>
              new Date(b.dataEntrada || b.dataManutencao).getTime() -
              new Date(a.dataEntrada || a.dataManutencao).getTime(),
          );
          this.loading = false;
          this.calculateKpis();
          this.applyFilters();
        },
        error: (err) => {
          this.rows = [];
          this.loading = false;
          this.feedback.showError(err, 'Erro ao carregar manutenções.');
        },
      });
  }

  // --- FILTROS E PESQUISA ---
  setFilter(filter: string): void {
    this.activeFilter = filter;
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.rows];

    // Filtro rápido por estado
    if (this.activeFilter === 'Em Curso') {
      filtered = filtered.filter((r) => r.manutencaoEstadoEnum === 2);
    } else if (this.activeFilter === 'Agendada') {
      filtered = filtered.filter((r) => r.manutencaoEstadoEnum === 1);
    } else if (this.activeFilter === 'Concluída') {
      filtered = filtered.filter((r) => r.manutencaoEstadoEnum === 5);
    } else if (this.activeFilter === 'Aguarda Peças') {
      filtered = filtered.filter((r) => r.manutencaoEstadoEnum === 3);
    }

    // Pesquisa por matrícula, tipo de serviço, oficina ou ref
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter((r) => {
        const eq = this.getEquipamentoInfo(r.equipamentoId);
        const equipLabel =
          `${eq.matricula} ${eq.marca} ${eq.modelo}`.toLowerCase();
        const servico = (r.descricaoServico || '').toLowerCase();
        const obs = (r.observacoes || '').toLowerCase();
        const ref = this.getRef(r).toLowerCase();
        const oficina = this.getOficinaNome(r).toLowerCase();
        const contacto = this.getOficinaContacto(r).toLowerCase();
        return (
          equipLabel.includes(term) ||
          servico.includes(term) ||
          obs.includes(term) ||
          ref.includes(term) ||
          oficina.includes(term) ||
          contacto.includes(term)
        );
      });
    }

    this.filteredRows = filtered;
  }

  // --- KPIs ---
  private calculateKpis(): void {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const emManutencao = this.rows.filter(
      (r) => r.manutencaoEstadoEnum === 2,
    ).length;
    const aguardamPecas = this.rows.filter(
      (r) => r.manutencaoEstadoEnum === 3,
    ).length;
    const agendadas = this.rows.filter(
      (r) => r.manutencaoEstadoEnum === 1,
    ).length;

    let concluidasMes = 0;
    let custoMes = 0;

    this.rows.forEach((r) => {
      if (r.manutencaoEstadoEnum === 5) {
        const refDate = r.dataSaida || r.dataManutencao;
        if (refDate) {
          const d = new Date(refDate);
          if (
            d.getMonth() === currentMonth &&
            d.getFullYear() === currentYear
          ) {
            concluidasMes++;
            custoMes += Number(r.custoReal ?? r.custo ?? 0);
          }
        }
      }
    });

    let revisoesVencidas = 0;
    this.equipamentos.forEach((eq) => {
      const proximaData = eq.propriedades?.find(
        (p) => p.nome?.toUpperCase() === 'PROXIMA_REVISAO_DATA',
      )?.valor;
      const proximaKm = eq.propriedades?.find(
        (p) => p.nome?.toUpperCase() === 'PROXIMA_REVISAO_KM',
      )?.valor;
      const kmAtual = eq.propriedades?.find(
        (p) => p.nome?.toUpperCase() === 'KM',
      )?.valor;

      let vencida = false;
      if (proximaData && new Date(String(proximaData)) < now) {
        vencida = true;
      }
      if (proximaKm && kmAtual && Number(kmAtual) >= Number(proximaKm)) {
        vencida = true;
      }

      // Se já não estiver em manutenção
      const temEmCurso = this.rows.some(
        (r) =>
          r.equipamentoId === eq.id &&
          (r.manutencaoEstadoEnum === 2 || r.manutencaoEstadoEnum === 3),
      );
      if (vencida && !temEmCurso) {
        revisoesVencidas++;
      }
    });

    let custoFormatado = 'AOA 0';
    if (custoMes >= 1000) {
      const emK = Math.round(custoMes / 1000);
      custoFormatado = `AOA ${emK}K`;
    } else if (custoMes > 0) {
      custoFormatado = `AOA ${custoMes}`;
    }

    this.kpiItems = [
      {
        icon: 'ri-truck-line',
        label: 'Em Manutenção',
        value: emManutencao,
        iconClass: 'kpi-icon-orange',
      },
      {
        icon: 'ri-time-line',
        label: 'Aguardam Peças',
        value: aguardamPecas,
        iconClass: 'kpi-icon-slate',
      },
      {
        icon: 'ri-checkbox-circle-line',
        label: 'Concluídas (mês)',
        value: concluidasMes,
        iconClass: 'kpi-icon-green',
      },
      {
        icon: 'ri-calendar-event-line',
        label: 'Agendadas',
        value: agendadas,
        iconClass: 'kpi-icon-blue',
      },
      {
        icon: 'ri-error-warning-line',
        label: 'Revisões Vencidas',
        value: revisoesVencidas,
        iconClass: 'kpi-icon-red',
      },
      {
        icon: 'ri-money-dollar-circle-line',
        label: 'Custo Total (mês)',
        value: custoFormatado,
        iconClass: 'kpi-icon-purple',
      },
    ];

    // Emite para a tab pai para exibir badge na sub-tab "Manutenção [X]"
    this.countChange.emit(emManutencao + aguardamPecas + agendadas);
  }

  // --- DRAWER NOVA / EDITAR MANUTENÇÃO ---
  openCreate(): void {
    this.editingId = null;
    this.submitted = false;
    this.selectedFiles = [];
    this.form = this.emptyForm();
    this.drawerVisible = true;
  }

  openEdit(row: ManutencaoEquipamento): void {
    this.editingId = row.id;
    this.submitted = false;
    this.selectedFiles = [];

    const dataEntradaVal = row.dataEntrada
      ? new Date(row.dataEntrada)
      : row.dataManutencao
        ? new Date(row.dataManutencao)
        : new Date();

    this.form = {
      equipamentoId: row.equipamentoId,
      dataManutencao: dataEntradaVal,
      dataEntrada: dataEntradaVal,
      dataPrevisaoSaida: row.dataPrevisaoSaida
        ? new Date(row.dataPrevisaoSaida)
        : null,
      dataSaida: row.dataSaida ? new Date(row.dataSaida) : null,
      manutencaoTipoEnum: row.manutencaoTipoEnum,
      manutencaoEstadoEnum: row.manutencaoEstadoEnum,
      custo: row.custo,
      custoEstimado: row.custoEstimado ?? row.custo,
      custoReal: row.custoReal,
      oficina: row.oficina || this.getOficina(row),
      oficinaContacto: row.oficinaContacto || '',
      descricaoServico: row.descricaoServico ?? '',
      observacoes: this.getObservacoesLimpas(row),
      quilometragemOuHorasUso: row.quilometragemOuHorasUso,
    };
    this.drawerVisible = true;
  }

  closeDrawer(): void {
    if (this.saving) return;
    this.drawerVisible = false;
  }

  save(): void {
    this.submitted = true;
    if (
      !this.form.equipamentoId ||
      !this.form.dataEntrada ||
      this.form.manutencaoTipoEnum === null ||
      this.form.manutencaoEstadoEnum === null
    ) {
      return;
    }

    const payload: ManutencaoEquipamentoForm = {
      ...this.form,
      dataManutencao: this.form.dataEntrada,
      custo: this.form.custo ?? this.form.custoEstimado ?? 0,
      observacoes: this.buildObservacoes(
        this.form.observacoes,
        this.form.oficina,
      ),
    };

    this.saving = true;
    const op$ = this.editingId
      ? this.service.atualizar(this.editingId, payload)
      : this.service.criar(payload, this.selectedFiles);

    op$
      .pipe(
        switchMap((res) => {
          if (this.editingId && this.selectedFiles.length) {
            const uploads = this.selectedFiles.map((file) =>
              this.service.adicionarAnexo(this.editingId!, file, file.name),
            );
            return forkJoin(uploads).pipe(switchMap(() => of(res)));
          }
          return of(res);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.saving = false;
          this.drawerVisible = false;
          this.feedback.showSuccess(
            this.editingId ? 'Manutenção atualizada!' : 'Manutenção registada!',
          );
          this.load();
        },
        error: (err) => {
          this.saving = false;
          this.feedback.showError(err, 'Erro ao guardar manutenção.');
        },
      });
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.selectedFiles.push(...Array.from(input.files));
      input.value = '';
    }
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  // --- DRAWER DE CONCLUSÃO ---
  openConcluir(row: ManutencaoEquipamento): void {
    this.concluindoRow = row;
    this.concluirSelectedFiles = [];
    this.concluirForm = {
      dataSaida: new Date(),
      custoReal: row.custoReal ?? row.custo ?? null,
      kmReal: row.quilometragemOuHorasUso || null,
      oficina: row.oficina || this.getOficina(row),
      oficinaContacto: row.oficinaContacto || '',
      observacoes: this.getObservacoesLimpas(row),
    };
    this.concluirDrawerVisible = true;
  }

  closeConcluirDrawer(): void {
    if (this.savingConclusao) return;
    this.concluirDrawerVisible = false;
    this.concluindoRow = null;
  }

  saveConclusao(): void {
    if (!this.concluindoRow || !this.concluirForm.dataSaida) return;
    this.savingConclusao = true;

    const payload: ManutencaoEquipamentoForm = {
      equipamentoId: this.concluindoRow.equipamentoId,
      dataManutencao: this.concluindoRow.dataManutencao
        ? new Date(this.concluindoRow.dataManutencao)
        : new Date(),
      dataEntrada: this.concluindoRow.dataEntrada
        ? new Date(this.concluindoRow.dataEntrada)
        : null,
      dataPrevisaoSaida: this.concluindoRow.dataPrevisaoSaida
        ? new Date(this.concluindoRow.dataPrevisaoSaida)
        : null,
      dataSaida: this.concluirForm.dataSaida,
      manutencaoTipoEnum: this.concluindoRow.manutencaoTipoEnum,
      manutencaoEstadoEnum: 5, // Concluída
      custo: this.concluirForm.custoReal ?? this.concluindoRow.custo ?? 0,
      custoEstimado:
        this.concluindoRow.custoEstimado ?? this.concluindoRow.custo,
      custoReal: this.concluirForm.custoReal ?? 0,
      oficina: this.concluirForm.oficina,
      oficinaContacto: this.concluirForm.oficinaContacto,
      descricaoServico: this.concluindoRow.descricaoServico,
      observacoes: this.buildObservacoes(
        this.concluirForm.observacoes,
        this.concluirForm.oficina,
      ),
      quilometragemOuHorasUso: this.concluirForm.kmReal,
    };

    this.service
      .atualizar(this.concluindoRow.id, payload)
      .pipe(
        switchMap((res) => {
          if (this.concluirSelectedFiles.length) {
            const uploads = this.concluirSelectedFiles.map((f) =>
              this.service.adicionarAnexo(this.concluindoRow!.id, f, f.name),
            );
            return forkJoin(uploads).pipe(switchMap(() => of(res)));
          }
          return of(res);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.savingConclusao = false;
          this.concluirDrawerVisible = false;
          this.concluindoRow = null;
          this.feedback.showSuccess('Manutenção concluída com sucesso!');
          this.load();
        },
        error: (err) => {
          this.savingConclusao = false;
          this.feedback.showError(
            err,
            'Não foi possível concluir a manutenção.',
          );
        },
      });
  }

  onConcluirFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.concluirSelectedFiles.push(...Array.from(input.files));
      input.value = '';
    }
  }

  removeConcluirFile(index: number) {
    this.concluirSelectedFiles.splice(index, 1);
  }

  // --- MENU DE AÇÕES (KEBAB) ---
  openRowMenu(event: Event, row: ManutencaoEquipamento): void {
    this.menuItems = [
      {
        icon: 'pi pi-eye',
        label: 'Ver detalhes',
        command: () => this.openVerDetalhe(row),
      },
      {
        icon: 'pi pi-check-circle',
        label: 'Concluir manutenção',
        visible: this.canConcluir(row),
        command: () => this.openConcluir(row),
      },
      {
        icon: 'pi pi-pencil',
        label: 'Editar',
        visible: this.canEditar(row),
        command: () => this.openEdit(row),
      },
    ];
    this.rowMenu.toggle(event);
  }

  // --- DRAWER VER DETALHES ---
  openVerDetalhe(row: ManutencaoEquipamento): void {
    this.detalheRow = row;
    this.detalheDrawerVisible = true;
  }

  closeDetalheDrawer(): void {
    this.detalheDrawerVisible = false;
    this.detalheRow = null;
  }

  // --- HELPERS E FORMATAÇÃO ---
  private emptyForm(): ManutencaoEquipamentoForm {
    return {
      equipamentoId: null,
      dataManutencao: new Date(),
      dataEntrada: new Date(),
      dataPrevisaoSaida: null,
      dataSaida: null,
      manutencaoTipoEnum: 1,
      manutencaoEstadoEnum: 2, // Default: Em Curso
      custo: null,
      custoEstimado: null,
      custoReal: null,
      oficina: '',
      oficinaContacto: '',
      descricaoServico: '',
      observacoes: '',
      quilometragemOuHorasUso: null,
    };
  }

  getRef(row: ManutencaoEquipamento): string {
    if (row.referencia) return row.referencia;
    const year = new Date(
      row.dataEntrada || row.dataManutencao || Date.now(),
    ).getFullYear();
    return `MAN-${year}-${String(row.id).padStart(3, '0')}`;
  }

  private eqInfoCache = new Map<number, any>();

  getEquipamentoInfoCache(id: number) {
    if (this.eqInfoCache.has(id)) {
      return this.eqInfoCache.get(id);
    }
    const info = this.getEquipamentoInfo(id);
    this.eqInfoCache.set(id, info);
    return info;
  }

  getEquipamentoInfo(id: number) {
    const eq = this.equipamentos.find((e) => e.id === id);
    if (!eq) return { matricula: 'Desc.', marca: '', modelo: '', foto: null };
    return {
      matricula: this.getMatricula(eq),
      marca:
        eq.propriedades?.find((p) => p.nome?.toUpperCase() === 'MARCA')
          ?.valor || '',
      modelo:
        eq.propriedades?.find((p) => p.nome?.toUpperCase() === 'MODELO')
          ?.valor || '',
      foto: eq.fotoUrl,
    };
  }

  getMatricula(e: Equipamento): string {
    const m = e.propriedades?.find(
      (p) => p.nome?.toUpperCase() === 'MATRICULA',
    )?.valor;
    return m ? String(m).toUpperCase() : 'S/ MAT';
  }

  getMarcaModelo(e: Equipamento): string {
    const marca =
      e.propriedades?.find((p) => p.nome?.toUpperCase() === 'MARCA')?.valor ||
      '';
    const modelo =
      e.propriedades?.find((p) => p.nome?.toUpperCase() === 'MODELO')?.valor ||
      '';
    if (!marca && !modelo) return 'Veículo';
    return `${marca} ${modelo}`.trim();
  }

  getOficinaNome(row: ManutencaoEquipamento): string {
    if (row.oficina) return row.oficina;
    const parsed = this.getOficina(row);
    return parsed || 'Oficina Geral';
  }

  getOficinaContacto(row: ManutencaoEquipamento): string {
    if (row.oficinaContacto) return row.oficinaContacto;
    return '';
  }

  getOficina(row?: ManutencaoEquipamento | null): string {
    if (!row || !row.observacoes) return '';
    const match = row.observacoes.match(/\[Oficina:\s*([^\]]+)\]/i);
    if (match) return match[1].trim();
    return '';
  }

  getObservacoesLimpas(row?: ManutencaoEquipamento | null): string {
    if (!row || !row.observacoes) return '';
    return row.observacoes.replace(/\[Oficina:\s*[^\]]+\]/gi, '').trim();
  }

  private buildObservacoes(obs: string, oficina?: string): string {
    const clean = (obs || '').replace(/\[Oficina:\s*[^\]]+\]/gi, '').trim();
    if (oficina && oficina.trim()) {
      return `[Oficina: ${oficina.trim()}] ${clean}`.trim();
    }
    return clean;
  }

  getDataSaidaInfo(row?: ManutencaoEquipamento | null): {
    text: string;
    cssClass: string;
  } {
    if (!row) return { text: '—', cssClass: 'text-muted-dash' };
    if (row.manutencaoEstadoEnum === 5) {
      const data = row.dataSaida || row.dataManutencao;
      return {
        text: this.formatDate(data),
        cssClass: 'text-saida-concluida',
      };
    }
    if (row.manutencaoEstadoEnum === 3) {
      if (row.dataPrevisaoSaida) {
        return {
          text: 'Previsto: ' + this.formatShortDate(row.dataPrevisaoSaida),
          cssClass: 'text-saida-alerta',
        };
      }
      return { text: 'Aguarda peça importada', cssClass: 'text-saida-alerta' };
    }
    if (row.manutencaoEstadoEnum === 2) {
      if (row.dataPrevisaoSaida) {
        return {
          text: 'Previsto: ' + this.formatShortDate(row.dataPrevisaoSaida),
          cssClass: 'text-saida-previsao',
        };
      }
      return { text: 'Em curso', cssClass: 'text-saida-previsao' };
    }
    if (row.manutencaoEstadoEnum === 1) {
      if (row.dataPrevisaoSaida) {
        return {
          text: 'Previsto: ' + this.formatShortDate(row.dataPrevisaoSaida),
          cssClass: 'text-muted-dash',
        };
      }
      return { text: '—', cssClass: 'text-muted-dash' };
    }
    return { text: '—', cssClass: 'text-muted-dash' };
  }

  formatShortDate(value?: string | Date | null): string {
    if (!value) return '—';
    try {
      const d = value instanceof Date ? value : new Date(value);
      const dia = String(d.getDate()).padStart(2, '0');
      const mes = String(d.getMonth() + 1).padStart(2, '0');
      return `${dia}/${mes}`;
    } catch {
      return String(value);
    }
  }

  isConcluida(row?: ManutencaoEquipamento | null): boolean {
    if (!row) return false;
    return row.manutencaoEstadoEnum === 5;
  }

  getCustoValorPrincipal(row?: ManutencaoEquipamento | null): string {
    if (!row) return '0';
    const val = row.custoReal ?? row.custo ?? row.custoEstimado ?? 0;
    return this.formatNumber(val);
  }

  getCustoEstimado(row?: ManutencaoEquipamento | null): string {
    if (!row) return '0';
    const val = row.custoEstimado ?? row.custo ?? 0;
    return this.formatNumber(val);
  }

  getCustoReal(row?: ManutencaoEquipamento | null): string {
    if (!row) return '—';
    if (row.custoReal !== null && row.custoReal !== undefined) {
      return this.formatNumber(row.custoReal);
    }
    return '—';
  }

  formatNumber(val: number): string {
    return new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(
      val,
    );
  }

  getEstadoInfo(value?: number | null): {
    label: string;
    dotClass: string;
    badgeClass: string;
  } {
    if (value === null || value === undefined) {
      return {
        label: 'Desconhecido',
        dotClass: 'dot-gray',
        badgeClass: 'badge-default',
      };
    }
    switch (value) {
      case 1:
        return {
          label: 'Agendada',
          dotClass: 'dot-blue',
          badgeClass: 'badge-agendada',
        };
      case 2:
        return {
          label: 'Em Curso',
          dotClass: 'dot-amber',
          badgeClass: 'badge-em-curso',
        };
      case 3:
        return {
          label: 'Aguarda Peças',
          dotClass: 'dot-orange',
          badgeClass: 'badge-aguarda-pecas',
        };
      case 5:
        return {
          label: 'Concluída',
          dotClass: 'dot-green',
          badgeClass: 'badge-concluida',
        };
      default:
        return {
          label: this.getEstadoLabel(value),
          dotClass: 'dot-gray',
          badgeClass: 'badge-default',
        };
    }
  }

  canConcluir(row?: ManutencaoEquipamento | null): boolean {
    if (!row) return false;
    return row.manutencaoEstadoEnum === 2 || row.manutencaoEstadoEnum === 3;
  }

  canEditar(row?: ManutencaoEquipamento | null): boolean {
    if (!row) return false;
    return row.manutencaoEstadoEnum !== 5;
  }

  getTipoLabel(value: number): string {
    return this.tipos.find((item) => item.value === value)?.label ?? 'Revisão';
  }

  getEstadoLabel(value: number): string {
    return (
      this.estados.find((item) => item.value === value)?.label ?? 'Desconhecido'
    );
  }

  formatDate(value?: string | Date | null): string {
    if (!value) return '—';
    try {
      const d = value instanceof Date ? value : new Date(value);
      return d.toLocaleDateString('pt-AO');
    } catch {
      return String(value);
    }
  }

  formatKm(value?: number | null): string {
    if (value === null || value === undefined) return '—';
    return (
      new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(
        value,
      ) + ' km'
    );
  }
}
